import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MobileOptimizedInput } from './components/SmartFormField'
import NavigationBar from './components/NavigationBar'

type StudyCheckResponse = {
  exists?: boolean
  error?: string
}

export default function App() {
  const [studyId, setStudyId] = useState('')
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const normalizedStudyId = studyId.trim().toLowerCase()

  const validateAndProceed = async () => {
    // Validate study ID starts with "hand"
    if (!normalizedStudyId) {
      setError('Please enter a Study ID.')
      return
    }
    if (!normalizedStudyId.startsWith('hand')) {
      setError('Invalid Study ID. Please try again.')
      return
    }
    
    // Check for duplicate study ID
    try {
      const response = await fetch(`/api/submissions/check?study_id=${encodeURIComponent(normalizedStudyId)}`)
      if (response.ok) {
        const data = await response.json() as StudyCheckResponse
        if (data.exists) {
          setError('This Study ID has already been used. Please use a different one.')
          return
        }
      } else if (window.location.hostname !== 'localhost') {
        const data = await response.json().catch(() => ({} as StudyCheckResponse)) as StudyCheckResponse
        setError(data.error || 'Unable to validate Study ID. Please try again.')
        return
      }
    } catch (error) {
      // In development mode, API might not be available, so continue anyway
      if (window.location.hostname !== 'localhost') {
        setError('Network error. Please try again.')
        return
      }
    }
    
    // Store study ID and navigate to wizard
    localStorage.setItem('study_id', normalizedStudyId)
    navigate('/wizard')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    validateAndProceed()
  }

  // Check if study ID is valid for inline button
  const isValidStudyId = normalizedStudyId.startsWith('hand')

  return (
    <div 
      className="bg-white min-h-screen w-full mx-auto relative flex flex-col"
      style={{
        maxWidth: 'clamp(320px, 100vw, 420px)',
        minHeight: '100dvh'
      }}
    >
      {/* Header with Logo and Progress Bar - matching wizard structure */}
      <div 
        className="fixed inset-x-0 top-0 z-10 mx-auto"
        style={{
          maxWidth: 'clamp(320px, 100vw, 420px)',
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        {/* White header with logo */}
        <div className="bg-white h-[65px] w-full border-b border-border-light relative">
          <div className="flex items-center justify-center h-full">
            <div className="w-[160px] h-10">
              <div
                className="bg-center bg-cover bg-no-repeat size-full"
                style={{ backgroundImage: `url('/images/logo.png')` }}
              />
            </div>
          </div>
        </div>
        
        {/* Gray progress bar section */}
        <div className="bg-surface-muted h-1.5 w-full relative rounded-full">
          <div className="bg-brand-green h-1.5 left-0 top-0 absolute transition-all duration-300 rounded-full" style={{
            width: `${Math.round((1 / 24) * 100)}%`
          }} />
        </div>
      </div>

      {/* Scrollable Content Area - matching wizard layout */}
      <div 
        className="flex-1 overflow-y-auto"
        style={{
          paddingTop: `calc(71px + env(safe-area-inset-top))`, // 65px header + 6px progress bar
          paddingBottom: 'calc(100px + env(safe-area-inset-bottom))'
        }}
      >
        <div className="w-full max-w-[340px] mx-auto px-4 pt-8 pb-8 min-h-full flex flex-col justify-start">
          {/* Title with mobile-optimized typography */}
          <div className="text-center mb-8">
            <h2
              className="font-bold text-black text-fluid-xl"
            >
              Please enter your study ID:
            </h2>
          </div>
          
          {/* Mobile-optimized form field */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col items-center space-y-4">
              <div className="w-full max-w-[309px]">
                <MobileOptimizedInput
                  type="text"
                  value={studyId}
                  onChange={(e) => {
                    setStudyId(e.target.value)
                    setError('')
                  }}
                  placeholder="Study ID"
                  onEnterPress={validateAndProceed}
                  style={{
                    fontSize: '16px',
                    padding: '12px 16px'
                  }}
                />
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center w-full max-w-[309px] px-2">
                  {error}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Universal Navigation Bar - matching wizard implementation */}
      <NavigationBar
        step={1}
        totalSteps={24}
        canProceed={isValidStudyId}
        onBack={() => {}} // No-op since this is first step
        onNext={validateAndProceed}
        isFirstStep={true}
        isLastStep={false}
      />
    </div>
  )
}
